import React, { useCallback, useMemo } from 'react'
import { makeStyles } from '@material-ui/styles'
import { IconButton, Typography } from '@material-ui/core'
import { ChevronLeft, ChevronRight } from '@material-ui/icons'
import { useSelector } from 'react-redux'
import { RootState } from 'app/store'
import DataKeys from 'k8s/DataKeys'
import Dropdown from 'core/elements/dropdown'

const useStyles = makeStyles((theme: any) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  info: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  pageSizeControl: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  dropdown: {
    display: 'inline-flex',
    flexFlow: 'row nowrap',
    justifyContent: 'right',
    alignItems: 'center',
    '& .inputFrame': {
      borderWidth: 1,
      marginRight: theme.spacing(1),
    },
    '& .placeholder': {
      fontWeight: 400,
    },
    '& .dropdownMenu': {
      minWidth: 50,
    },
  },
}))

interface VMServerSidePaginationProps {
  dataKey: DataKeys
  currentPage: number
  pageSize: number
  onPageChange: (newPage: number, marker?: string) => void
  onPageSizeChange: (newPageSize: number) => void
  totalCount?: number
  hasNextPage?: boolean
  nextMarker?: string | null
}

const PAGE_SIZE_OPTIONS = [5, 10, 25].map((value) => ({ value }))

export default function VMServerSidePagination({
  dataKey,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  totalCount,
  hasNextPage: hasNextPageProp,
  nextMarker: nextMarkerProp,
}: VMServerSidePaginationProps) {
  const classes = useStyles()

  // Get pagination metadata from session state
  const paginationMeta = useSelector((state: RootState) => {
    const session = state.session

    // Map dataKey to corresponding session pagination field
    if (dataKey === DataKeys.AllOpenstackVirtualMachines) {
      return session?.allVmsPagination as
        | {
            currentMarker?: string | null
            nextMarker?: string | null
            hasMore?: boolean
            pageSize?: number
            totalCount?: number
          }
        | undefined
    } else if (dataKey === DataKeys.SelfOpenstackVirtualMachines) {
      return session?.selfVmsPagination as
        | {
            currentMarker?: string | null
            nextMarker?: string | null
            hasMore?: boolean
            pageSize?: number
            totalCount?: number
          }
        | undefined
    } else {
      return session?.vmsPagination as
        | {
            currentMarker?: string | null
            nextMarker?: string | null
            hasMore?: boolean
            pageSize?: number
            totalCount?: number
          }
        | undefined
    }
  })

  // Get current loaded data count
  const loadedCount = useSelector((state: RootState) => {
    const cachedData = state.cache?.cachedData?.[dataKey]?.ids
    return Array.isArray(cachedData) ? cachedData.length : 0
  })

  // Use total count with priority:
  // 1. Session pagination totalCount (from search)
  // 2. totalCount prop (from lite API)
  // 3. loadedCount (fallback)
  const displayTotal =
    paginationMeta?.totalCount != null
      ? paginationMeta.totalCount
      : totalCount != null
      ? totalCount
      : loadedCount

  // Prefer props over session state for more accurate page state
  const hasNextPage = useMemo(() => {
    // If parent provides explicit hasNextPage state, use it
    if (hasNextPageProp !== undefined) {
      return hasNextPageProp
    }

    // Check if there are more records based on total count
    if (displayTotal != null) {
      const lastRecordOnCurrentPage = (currentPage + 1) * pageSize
      return lastRecordOnCurrentPage < displayTotal
    }

    // Fallback to session state
    return paginationMeta?.hasMore === true && paginationMeta?.nextMarker != null
  }, [hasNextPageProp, paginationMeta, displayTotal, currentPage, pageSize])

  const hasPrevPage = useMemo(() => {
    return currentPage > 0
  }, [currentPage])

  const handleNext = useCallback(() => {
    if (hasNextPage) {
      // Use prop marker if available, otherwise fall back to session state
      const marker = nextMarkerProp || paginationMeta?.nextMarker
      onPageChange(currentPage + 1, marker)
    }
  }, [hasNextPage, nextMarkerProp, paginationMeta, currentPage, onPageChange])

  const handlePrev = useCallback(() => {
    if (hasPrevPage) {
      onPageChange(currentPage - 1)
    }
  }, [hasPrevPage, currentPage, onPageChange])

  const showLoadedSuffix =
    paginationMeta?.totalCount == null && totalCount == null && loadedCount > 0

  // Calculate display range - ensure endRecord doesn't exceed actual total
  const startRecord = displayTotal > 0 ? currentPage * pageSize + 1 : 0
  const endRecord = Math.min((currentPage + 1) * pageSize, displayTotal)

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      onPageSizeChange(newSize)
    },
    [onPageSizeChange],
  )

  return (
    <div className={classes.root}>
      <Typography className={classes.info}>
        {loadedCount > 0
          ? `Showing ${startRecord}-${endRecord} of ${displayTotal}${
              showLoadedSuffix ? ' loaded' : ''
            }`
          : 'No records'}
      </Typography>
      <div className={classes.controls}>
        <IconButton
          onClick={handlePrev}
          disabled={!hasPrevPage}
          size="small"
          aria-label="Previous page"
        >
          <ChevronLeft />
        </IconButton>
        <Typography className={classes.info}>Page {currentPage + 1}</Typography>
        <IconButton
          onClick={handleNext}
          disabled={!hasNextPage}
          size="small"
          aria-label="Next page"
        >
          <ChevronRight />
        </IconButton>
      </div>
      <div className={classes.pageSizeControl}>
        <Dropdown
          className={classes.dropdown}
          label="Results per page"
          compact
          items={PAGE_SIZE_OPTIONS}
          value={pageSize}
          onChange={handlePageSizeChange}
        />
      </div>
    </div>
  )
}
